class ProductOptions extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.available_variants = [];
    this.container = this.querySelector(".product-options--container");
    this.first_variant = JSON.parse(
      this.querySelector("[id*=variant-first--json]").innerHTML
    );
    this.inputs = this.container.querySelectorAll(
      '[data-item="disclosure"], [data-item="radio"]'
    );
    this.is_product_page =
      null !== this.closest(".product-page--root");
    this.option_values = [];
    this.size_chart = this.querySelector(
      ".product-options--option .product-size-chart--root"
    );
    this.variants = JSON.parse(
      this.querySelector("[id*=variant--json]").innerHTML
    );
    this.getVariantOptions();
    this.hideOptions(this.first_variant, 0);
    this.disableEmptyOptions();
    this.optionChangeListener();
    this.size_chart && this.moveSizeChart();
  }

  disconnectedCallback() {
    this.inputs.off("change.ProductOptions");
  }

  getVariantOptions() {
    if (
      this.variants.forEach(
        (t) => t.available && this.available_variants.push(t.options)
      ),
      0 < this.available_variants.length
    )
      for (let i = 0; i < this.available_variants[0].length; i++)
        this.option_values[i] = [
          ...new Set(this.available_variants.map((t) => t[i])),
        ];
  }

  disableEmptyOptions() {
    const e = this.option_values.flat();
    this.container
      .querySelectorAll(".radios--input, .disclosure--option")
      .forEach((t) => {
        var i = this.getOptionValue(t);
        e.includes(i) || t.setAttribute("data-empty", !0);
      });
  }

  isElementVisible(element) {
    // Check computed style - if display is none, element is hidden
    const style = window.getComputedStyle(element);
    if (style.display === "none" || style.visibility === "hidden") {
      return false;
    }
    // Check parent with data-mq attribute
    const mqParent = element.closest("[data-mq]");
    if (mqParent) {
      const mq = mqParent.getAttribute("data-mq");
      const currentWindow = theme.mqs?.current_window || "small";
      // Check if the mq attribute matches current viewport
      if (mq === "small" && (currentWindow === "medium" || currentWindow === "large")) {
        return false;
      }
      if (mq === "medium-large" && currentWindow === "small") {
        return false;
      }
    }
    // Use offsetParent as final check (null means hidden)
    return element.offsetParent !== null;
  }

  getVisibleInputs() {
    // Get all inputs, but only return visible ones
    const allInputs = this.container.querySelectorAll(
      '[data-item="disclosure"], [data-item="radio"]'
    );
    return Array.from(allInputs).filter((input) => {
      // For radio inputs, check if checked and visible
      if (input.type === "radio") {
        return input.checked && this.isElementVisible(input);
      }
      // For disclosure inputs (hidden), check if parent disclosure is visible
      if (input.hasAttribute("data-item") && input.getAttribute("data-item") === "disclosure") {
        const disclosureRoot = input.closest("disclosure-root");
        return disclosureRoot && this.isElementVisible(disclosureRoot);
      }
      return this.isElementVisible(input);
    });
  }

  syncInputs(changedInput, newValue) {
    // Find the corresponding input in the other view and sync it
    // This syncs without triggering change events to avoid loops
    const optionContainer = changedInput.closest(".product-options--option");
    if (!optionContainer) return;

    const isRadio = changedInput.type === "radio";
    const isDisclosure = changedInput.hasAttribute("data-item") && 
                        changedInput.getAttribute("data-item") === "disclosure";

    // Get the data-mq container of the changed input (if any)
    const changedMqContainer = changedInput.closest("[data-mq]");
    const changedMq = changedMqContainer ? changedMqContainer.getAttribute("data-mq") : null;

    // Find all inputs in this option container
    const allInputsInOption = optionContainer.querySelectorAll(
      '[data-item="disclosure"], [data-item="radio"]'
    );

    allInputsInOption.forEach((input) => {
      if (input === changedInput) return; // Skip the input that changed

      const inputIsRadio = input.type === "radio";
      const inputIsDisclosure = input.hasAttribute("data-item") && 
                               input.getAttribute("data-item") === "disclosure";

      // Get the data-mq container of this input (if any)
      const inputMqContainer = input.closest("[data-mq]");
      const inputMq = inputMqContainer ? inputMqContainer.getAttribute("data-mq") : null;

      // In "mixed" mode, sync between different views (different data-mq values)
      // Desktop has data-mq="medium-large" with radios, mobile has data-mq="small" with disclosure
      const isDifferentView = changedMq !== inputMq && changedMq !== null && inputMq !== null;
      const isSameView = changedMq === inputMq && changedMq !== null;
      const noMqContainers = changedMq === null && inputMq === null;

      // Determine if we should sync
      let shouldSync = false;
      
      if (isDifferentView) {
        // Different views (desktop vs mobile): sync radio <-> disclosure
        shouldSync = (isRadio && inputIsDisclosure) || (isDisclosure && inputIsRadio);
      } else if (isSameView) {
        // Same view: handle radio groups (uncheck other radios in same group)
        if (isRadio && inputIsRadio && input.value !== newValue) {
          // Uncheck other radios in the same group
          input.checked = false;
        }
        // Don't sync same-type disclosures in same view
      } else if (noMqContainers) {
        // No data-mq containers (e.g., color swatches): handle radio groups
        if (isRadio && inputIsRadio && input.value !== newValue) {
          input.checked = false;
        }
      }

      if (shouldSync) {
        if (inputIsRadio) {
          // Update radio button state
          if (input.value === newValue && !input.checked) {
            input.checked = true;
          } else if (input.value !== newValue && input.checked) {
            input.checked = false;
          }
        } else if (inputIsDisclosure) {
          // For disclosure, update the hidden input value
          if (input.value !== newValue) {
            input.value = newValue;
            // Also update the disclosure display if it exists
            const disclosureRoot = input.closest("disclosure-root");
            if (disclosureRoot) {
              const currentOptionEl = disclosureRoot.querySelector(
                `.disclosure--option[data-value="${newValue}"]`
              );
              if (currentOptionEl) {
                const currentOptionText = currentOptionEl.innerHTML;
                const currentOptionDiv = disclosureRoot.querySelector(
                  ".disclosure--current-option"
                );
                if (currentOptionDiv) {
                  currentOptionDiv.innerHTML = currentOptionText;
                }
                // Update aria-current
                disclosureRoot
                  .querySelectorAll(".disclosure--option")
                  .forEach((opt) => {
                    opt.setAttribute(
                      "aria-current",
                      opt === currentOptionEl ? "true" : "false"
                    );
                  });
              }
            }
          }
        }
      }
    });
  }

  optionChangeListener() {
    this.inputs.on("change.ProductOptions", ({ target: t }) => {
      const newValue = t.value;
      
      // Sync the corresponding input in the other view
      this.syncInputs(t, newValue);

      // Collect current values from visible inputs only, ensuring one per option
      const a = [];
      const optionValuesMap = new Map(); // Map option index to value

      // First pass: collect from visible inputs
      // For radios, get checked ones. For disclosures, get the hidden input value
      this.container
        .querySelectorAll('[data-item="disclosure"], [data-item="radio"]')
        .forEach((input) => {
          const optionContainer = input.closest(".product-options--option");
          if (!optionContainer) return;
          
          const optionIndex = parseInt(optionContainer.dataset.index);
          
          // Skip if we already have a value for this option index
          if (optionValuesMap.has(optionIndex)) return;
          
          // For radio inputs, only use if checked and visible
          if (input.type === "radio") {
            if (input.checked && this.isElementVisible(input)) {
              optionValuesMap.set(optionIndex, input.value);
            }
          }
          // For disclosure inputs (hidden), check if parent disclosure is visible
          else if (input.hasAttribute("data-item") && 
                   input.getAttribute("data-item") === "disclosure") {
            const disclosureRoot = input.closest("disclosure-root");
            if (disclosureRoot && this.isElementVisible(disclosureRoot) && input.value) {
              optionValuesMap.set(optionIndex, input.value);
            }
          }
        });

      // Convert map to array
      optionValuesMap.forEach((value, index) => {
        a.push({ index: index, value: value });
      });

      // Sort by index to ensure correct order
      a.sort((x, y) => x.index - y.index);

      const changedOptionIndex = parseInt(
        t.closest(".product-options--option").dataset.index
      );

      this.hideOptions(
        a.map((t) => t.value),
        changedOptionIndex
      );

      let s = !1;
      this.variants.every((i) => {
        let e = !0;
        return (
          a.forEach((t) => {
            e = e && i.options[t.index] === t.value;
          }),
          !e || ((s = i), !1)
        );
      });
      this.updateVariant(s);
    });
  }

  updateVariant(t) {
    this.is_product_page && this.updateHistoryState(t.id);
    window.trigger(`theme:product:${this.dataset.id}:variantChanged`, {
      variant: t,
    });
  }

  updateHistoryState(t) {
    let i = "" + location.origin + location.pathname;
    t && (i += "?variant=" + t), history.replaceState({ path: i }, "", i);
  }

  hideOptions(a, i) {
    // Ensure we have valid option_values for all indices
    if (this.option_values.length === 0) {
      return;
    }

    const s =
      a.length === 0
        ? []
        : a.map((t, e) => {
            // Ensure option_values[e] exists
            if (!this.option_values[e] || !Array.isArray(this.option_values[e])) {
              return [];
            }
            return this.option_values[e].filter((t) => {
              const i = [...a];
              return (
                (i[e] = t),
                this.available_variants.some((t) => t.join(",") === i.join(","))
              );
            });
          });

    this.container.childNodes.forEach((t, e) => {
      // Ensure we have a valid array for this index
      if (e !== i && s[e] && Array.isArray(s[e])) {
        t.querySelectorAll(".radios--input, .disclosure--option").forEach(
          (t) => {
            var i = this.getOptionValue(t);
            t.setAttribute("data-unavailable", !s[e].includes(i));
          }
        );
      }
    });
  }

  getOptionValue(t) {
    return t.getAttribute("data-value") ? t.getAttribute("data-value") : t.value;
  }

  moveSizeChart() {
    this.size_chart.parentNode.querySelector("label").parentNode.appendChild(
      this.size_chart
    ),
      this.size_chart.removeAttribute("style");
  }
}

const productOptionsEl = customElements.get("product-options-root");
productOptionsEl ||
  customElements.define("product-options-root", ProductOptions);
